/*
 * @Author: nhsoft.wh
 * @Date: 2023-05-16 16:01:01
 * @LastEditors: 赵泽文
 * @LastEditTime: 2026-03-27 15:49:01
 * @Description: 多域名api聚合
 */

import type { ILoginInfo } from 'mars-front-api-sdk/types/interfaces/endpoints/Account';

import { delAllCookie, getClient, getCookie, getCookieHeader, setCookie } from './utils';
import { sessionCache } from './storageHandler';
import * as Sentry from '@sentry/react';

export interface ILogin {
  id?: string;
  first_name?: string;
  second_name?: string;
  phone?: string;
  email?: any;
  access_token?: string;
  refresh_token?: string;
  userDetails?: any;
}

export interface IGetClientProps {
  accessToken?: string;
  refreshToken?: string;
  merchantId: string;
  operator: string;
  deviceId: string;
  grant_type?: string;
  client_id?: string;
  client_secret?: string;
  [key: string]: any;
}

/**
 * 校验ip地址格式是否合法
 * @param ip ip地址
 * @returns {boolean}
 */
function isValidIP(ip: string): boolean {
  const ipv4Pattern =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const ipv6Pattern = /^(?:[a-f\d]{1,4}:){7}[a-f\d]{1,4}$/;

  if (ipv4Pattern.test(ip) || ipv6Pattern.test(ip)) {
    return true;
  }
  return false;
}

// LOGIN:登录层
// MERCHANT:应用层
export type TTokenLayer = 'LOGIN' | 'MERCHANT';

/**
 * 获取上级公用域名
 * @returns
 */
export const getToperDomain = () => {
  const hostName = window.location.hostname;
  // 第一个.的索引值
  const firstIndexOfDot = hostName.indexOf('.');

  // 公用cookie域名
  const publicCookieHostName = firstIndexOfDot === -1 ? hostName : hostName.slice(firstIndexOfDot);

  return publicCookieHostName;
};

/**
 * 删除指定的cookie
 * @param key cookie的键
 * @param domain 指定cookie的域名
 */
export const deleteSpecifiedCookie = (key: string, domain?: string) => {
  const exp = new Date();

  exp.setTime(exp.getTime() - 1);

  const cookieValue = getCookie(key);

  if (cookieValue != null) {
    sessionCache.removeItem(key);
    document.cookie = `${key}=${cookieValue}; expires=${exp.toUTCString()}; path=${escape(
      '/',
    )}; domain=${domain || window.location.hostname}`;
  }
};

/**
 * 登录成功后保存登录数据
 * @param data 登录数据
 */
export const saveLogin = (data: ILogin) => {
  const operator = `${data.userDetails.full_name}`;

  const accountId = data?.userDetails?.id;

  const topestDomain = getToperDomain();

  if (data.access_token) {
    setCookie('access_token', data.access_token, 7, topestDomain);
  }
  if (data.refresh_token) {
    setCookie('refresh_token', data.refresh_token, 7, topestDomain);
  }
  if (data?.phone || data?.userDetails?.phone) {
    setCookie('user_phone', data?.phone || data?.userDetails?.phone, 365, topestDomain);
  }
  if (operator) {
    setCookie('user_operator', operator, 365, topestDomain);
  }
  if (accountId) {
    setCookie('account_id', accountId, 7, topestDomain);
  }
};

/**
 * 登出
 */
export const logout = () => {
  Sentry.withScope((scope) => {
    scope.setLevel('info'); // 可选的，设置错误级别
    Sentry.captureMessage('multipleDomain的logout清空cookie', scope);
  });

  delAllCookie({
    excludeKeys: ['DEVICE_ID'],
  });

  const merchant_id = getCookie('merchant_id');

  localStorage.removeItem(`${merchant_id}_access_token`);
  localStorage.removeItem(`${merchant_id}_refresh_token`);
};

/**
 * 获取公用cookie域名
 * @returns
 */
export const getPublicCookieHostName = () => {
  let hostName = window.location.hostname;
  // 检查是否是ip
  const isIP = isValidIP(hostName);
  // 如果是ip，将ip替换为预设域名
  if (isIP) {
    hostName = LOGIN_PAGE_HOST_NAME;
  }
  // 第一个.的索引值
  const firstIndexOfDot = hostName.indexOf('.');
  // 公用cookie域名
  return firstIndexOfDot === -1 ? hostName : hostName.slice(firstIndexOfDot);
};

/**
 * 本地调试项目时自动切换域名到localhost
 * @param originHostName 原域名
 * @returns
 */
export const autoSwitchHostNameWhenDebug = (originHostName: string) => {
  const hostName = getPublicCookieHostName();

  return `${hostName === 'localhost' ? 'localhost' : originHostName}`;
};

/**
 * 二次登录；转移cookie；重新注册marsClient
 * @param merchant_id 商户艾迪
 * @param appointLocationType 指定类型：区域 | 门店
 * @returns
 */
export const merchantLogin = async (
  merchant_id: string,
  appointLocationType?: 'ZONE' | 'LOCATION',
) => {
  const params: any = {
    grant_type: 'account_access_token',
    client_id: window.MARS_BASE_CONFIG.MERCHANT_CLIENT_ID,
    client_secret: window.MARS_BASE_CONFIG.MERCHANT_CLIENT_SECRET,
    access_token: getCookie('access_token'),
    merchant_id,
  };
  const locationId = getCookie('location_id');

  const locationZoneId = getCookie('location_zone_id');

  if (appointLocationType) {
    if (appointLocationType === 'ZONE') {
      params.location_zone_id = locationZoneId;
    } else if (appointLocationType === 'LOCATION') {
      params.location_id = locationId;
    }
  } else if (locationZoneId) {
    params.location_zone_id = locationZoneId;
  } else if (locationId) {
    params.location_id = locationId;
  }

  getClient({
    ...getCookieHeader(),
    accessToken: undefined,
    client_id: window.MARS_BASE_CONFIG.MERCHANT_CLIENT_ID,
    client_secret: window.MARS_BASE_CONFIG.MERCHANT_CLIENT_SECRET,
  });

  const res = await window.marsClient.account.login.login(params);

  if (res.isSuccess() && res.success().data) {
    const result = res.success().data;

    setCookie(`${merchant_id}_access_token`, result?.access_token, 7, getToperDomain());
    setCookie(`${merchant_id}_refresh_token`, result?.refresh_token, 7, getToperDomain());

    const inherentHeader = getCookieHeader();

    getClient({
      ...inherentHeader,
      accessToken: result?.access_token,
      refreshToken: result?.refresh_token,
      grant_type: 'account_access_token',
    });

    return result;
  }

  return null;
};

/**
 * 获取当前sdk绑定的token所属的层级
 * @returns
 */
export const getCurrentTokenLayer: () => TTokenLayer = () => {
  const hostName = window.location.hostname;
  // 第一个.的索引值
  const firstIndexOfDot = hostName.indexOf('.');
  // 是否在登录层
  const firstPartOfHostName = window.location.host.slice(0, firstIndexOfDot);
  const isInLoginLayer =
    firstIndexOfDot === -1
      ? true
      : ['mars-portal', 'account', 'mars-chain-stock-portal'].includes(firstPartOfHostName);

  return isInLoginLayer ? 'LOGIN' : 'MERCHANT';
};

/**
 * 转移cookie
 */
export const transferCookie = () => {
  // 商户id
  const merchantId = getCookie('merchant_id');
  // 商户id对应的随机字符串
  const merchantIdNonce = getCookie('merchant_domain');
  // 商户级域名
  const merchantDomain = `${merchantIdNonce}${getToperDomain()}`;
  // 转移前需要删除的cookie
  const needDeleteCookies: string[] = [];
  console.log(getCookie('location_is'), 'locationOs');

  // 如果是门店，要删除location_zone_id(主要是防止两个浏览器tab同时处于merchant/shopList页，然后分别选择了同一家店铺的总部和门店，导致门店读到了location_zone_id)
  if (getCookie('location_is') === '1') {
    needDeleteCookies.push('location_zone_id');
  }
  // 不需要转移的cookie的键集合（登录那边的cookie）
  const cookiesThatDoNotRequireTransfer = [
    'operator_id',
    'access_token',
    'user_operator',
    'account_id',
    'DEVICE_ID',
    'refresh_token',
  ];
  // 需要存入localStorage的cookie
  const cookiesThatRequireTransferToLocalStorage = [
    `${merchantId}_access_token`,
    `${merchantId}_refresh_token`,
  ];
  // 拆分出来的cookie数组
  const cookiesArr = document.cookie.split('; ').map((cookie) => cookie.split('='));
  // 需要转移的cookie
  const needTransferCookies = cookiesArr.filter(
    (cookie) =>
      !cookiesThatDoNotRequireTransfer.includes(cookie[0]) &&
      !needDeleteCookies.includes(cookie[0]),
  );
  Sentry.withScope((scope) => {
    scope.setLevel('info'); // 可选的，设置错误级别
    Sentry.captureMessage('transferCookie转移前删除指定cookie', scope);
  });

  console.log(
    `【transferCookie】转移前的merchantId:${getCookie('merchantId')},operator_id:${getCookie(
      'operator_id',
    )}`,
  );
  console.log('transferCookie转移前删除指定cookie');
  // 转移前删除指定cookie
  needDeleteCookies.forEach((needDeleteCookie) => {
    // deleteSpecifiedCookie(needDeleteCookie, getToperDomain())
    deleteSpecifiedCookie(needDeleteCookie, window.location.hostname);
  });
  Sentry.captureMessage('【transferCookie】第一批次cookie删除完成');
  console.log(
    `【transferCookie】第一批次cookie删除完成后的merchantId:${getCookie(
      'merchantId',
    )},operator_id:${getCookie('operator_id')}`,
  );
  console.log('【transferCookie】第一批次cookie删除完成');
  // 转移前删除旧的cookie
  needTransferCookies.forEach((needTransferCookie) => {
    deleteSpecifiedCookie(needTransferCookie[0], getToperDomain());
  });
  Sentry.captureMessage('【transferCookie】第二批次cookie删除完成');
  console.log('【transferCookie】第二批次cookie删除完成');
  console.log(
    `【transferCookie】第二批次cookie删除完成后的merchantId:${getCookie(
      'merchantId',
    )},operator_id:${getCookie('operator_id')}`,
  );
  // 开始转移
  needTransferCookies.forEach((needTransferCookie) => {
    // 需要转存到当前域名localStorage的cookie
    if (cookiesThatRequireTransferToLocalStorage.includes(needTransferCookie[0])) {
      localStorage.setItem(needTransferCookie[0], unescape(needTransferCookie[1]));
    } else {
      // 需要转存到当前域名的cookie
      setCookie(needTransferCookie[0], unescape(needTransferCookie[1]) || '', 7, merchantDomain);
    }
  });
  Sentry.captureMessage('【transferCookie】cookie复制到商户域名下完成');
  console.log(
    `【transferCookie】cookie复制到商户域名下完成,merchantId:${getCookie(
      'merchantId',
    )},operator_id:${getCookie('operator_id')}`,
  );
  // 一级域名的cookie转存到本地缓存
  cookiesThatDoNotRequireTransfer.forEach((cookie) => {
    sessionCache.setItem(cookie, getCookie(cookie) || '');
  });
  // account_id复制一份到商户下，方便校验商户对应的用户是否改变
  setCookie(`${merchantId}_account_id`, getCookie('account_id') || '', 7, merchantDomain);
  Sentry.captureMessage('【transferCookie】merchantId_account_id注入cookie完成');
  console.log(`【transferCookie】merchantId_account_id${`${merchantId}_account_id`}注入cookie完成`);
  console.log(
    `【transferCookie】merchantId_account_id注入cookie完成后的merchantId:${getCookie(
      'merchantId',
    )},operator_id:${getCookie('operator_id')}`,
  );
};

/**
 * token过期刷新逻辑
 * @param data 登录信息
 */
export const handleTokenFresh = (data?: ILoginInfo) => {
  // 公用cookie域名
  const publicCookieHostName = getPublicCookieHostName();
  // 是否在登录层
  const isInLoginLayer = getCurrentTokenLayer() === 'LOGIN';

  if (data) {
    // 切换sdk绑定的token
    getClient({
      ...(getCookieHeader() || {}),
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    });

    if (isInLoginLayer) {
      setCookie('access_token', data.access_token, 7, getToperDomain());
      setCookie('refresh_token', data.refresh_token, 7, getToperDomain());

      if (getCookie('user_operator')) {
        setCookie('user_operator', getCookie('user_operator'), 365, getToperDomain());
      }
    } else {
      const merchant_id = getCookie('merchant_id');

      localStorage.setItem(`${merchant_id}_access_token`, data?.access_token);
      localStorage.setItem(`${merchant_id}_refresh_token`, data?.refresh_token);

      if (getCookie('user_operator')) {
        setCookie('user_operator', getCookie('user_operator'), 365, getToperDomain());
      }
    }
  } else {
    if (isInLoginLayer || window?.location?.host?.includes?.('mars-portal')) {
      logout();

      window.location.href = `${window.location.protocol}//${autoSwitchHostNameWhenDebug(
        `account${publicCookieHostName}`,
      )}:${window.location.port}/login`;
    } else {
      // 切换sdk绑定的token为登录层token
      getClient({ ...(getCookieHeader() || {}), refreshToken: getCookie('refresh_token') });
      Sentry.withScope((scope) => {
        scope.setLevel('info'); // 可选的，设置错误级别
        Sentry.captureMessage('multipleDomain的handleTokenFresh清空cookie', scope);
      });
      delAllCookie({
        excludeKeys: [
          'DEVICE_ID',
          'access_token',
          'account_id',
          'operator_id',
          'refresh_token',
          'use_staff_name',
          'user_operator',
        ],
      });

      window.location.href = `${window.location.protocol}//${autoSwitchHostNameWhenDebug(
        `account${publicCookieHostName}`,
      )}:${window.location.port}/merchant/shopList`;
    }
  }
};
